import type { LearningType, LearningKindCollectionType } from "common/learning";

import type { Action, ActionSection } from "tds/actionsMenu";

export type ActionConfig = {
  eventData?: Record<string, string>;
  learning: LearningType;
  trailmixIsFollowing?: boolean;
  user?: { username: string };
};

export type ContentActionEventNames =
  | "favorite"
  | "unfavorite"
  | "trailmix_add"
  | "trailmix_follow"
  | "trailmix_unfollow"
  | "trailmix_share_settings"
  | "details";

export type ContentActionSection = ActionSection & {
  actions: ContentAction[];
};

export type ContentAction = Action & {
  authRequired?: boolean;
  name: ContentActionEventNames;
};

export type ContentActions = {
  action?: ContentAction;
  actions: ContentActionSection[];
};

export const favorite: ContentAction = {
  name: "favorite",
  label: "",
  icon: {
    sprite: "trailhead",
    symbol: "star-outline",
  },
  authRequired: true,
};

export const unfavorite: ContentAction = {
  name: "unfavorite",
  label: "",
  icon: {
    sprite: "trailhead",
    symbol: "star-fill",
    style: `
    --tds-icon_fill: var(--tds-color-brand);
   `,
  },
  authRequired: true,
};

export const details: ContentAction = {
  name: "details",
  label: "",
  icon: {
    sprite: "utility",
    symbol: "info",
  },
  authRequired: true,
};

// TODO: Waiting for experience API support
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const trailmixAdd: ContentAction = {
  name: "trailmix_add",
  label: "",
  icon: {
    sprite: "utility",
    symbol: "add",
  },
  authRequired: true,
};

export const trailmixFollow: ContentAction = {
  name: "trailmix_follow",
  label: "",
  icon: {
    sprite: "utility",
    symbol: "new",
  },
  authRequired: true,
};

export const trailmixUnfollow: ContentAction = {
  name: "trailmix_unfollow",
  label: "",
  icon: {
    sprite: "utility",
    symbol: "close",
  },
  authRequired: true,
};

export function getBaseConfig(options: {
  favorited?: boolean;
}): ContentActions {
  return {
    action: options.favorited ? unfavorite : favorite,
    actions: [
      {
        actions: [trailmixAdd],
      },
    ],
  };
}

export function getTrailmixConfig(options: {
  isFollowing: boolean;
}): ContentActions {
  return {
    action: options.isFollowing ? undefined : trailmixFollow,
    actions: [
      {
        actions: [
          ...(options.isFollowing ? [trailmixUnfollow] : []),
          // TODO: This should be a link and be derived from options
          {
            name: "trailmix_share_settings",
            label: "",
            icon: {
              sprite: "utility",
              symbol: "settings",
            },
          },
        ],
      },
    ],
  };
}

export function getLearningActions(
  config: ActionConfig
): ContentActions | null {
  switch (config.learning.type as LearningKindCollectionType) {
    case "MODULE":
    case "PROJECT":
    case "SUPERBADGE":
    case "TRAIL":
      return getBaseConfig({ favorited: config.learning.favorite });
    case "TRAILMIX":
    case "LEARNINGPATH":
      // Experience API does not currently support Trailmix or LearningPath actions
      return null;
    default:
      throw new Error(`Unsupported type: "${config.learning.type}"`);
  }
}
